import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
} from '@mui/material';
import {
  Business,
  People,
  AttachMoney,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { THEME_COLORS } from '../../constants';

/**
 * Componente de estadísticas de distribuidores
 */
const DistributorStats = ({ stats }) => {
  const statsData = [
    {
      title: 'Total Distribuidores',
      value: stats.total || 0,
      icon: <Business sx={{ fontSize: '2rem', color: THEME_COLORS.TEXT_PRIMARY }} />,
      color: THEME_COLORS.TEXT_PRIMARY,
    },
    {
      title: 'Distribuidores Activos',
      value: stats.active || 0,
      icon: <CheckCircle sx={{ fontSize: '2rem', color: THEME_COLORS.TEXT_SECONDARY }} />,
      color: THEME_COLORS.TEXT_SECONDARY,
    },
    {
      title: 'Contactos Asignados',
      value: stats.total_contacts_assigned || 0,
      icon: <People sx={{ fontSize: '2rem', color: THEME_COLORS.SECONDARY }} />,
      color: THEME_COLORS.SECONDARY,
    },
    {
      title: 'Ingresos Totales',
      value: `$${(stats.total_revenue || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      icon: <AttachMoney sx={{ fontSize: '2rem', color: THEME_COLORS.TEXT_MUTED }} />,
      color: THEME_COLORS.TEXT_MUTED,
    },
  ];

  // Estadísticas adicionales para mostrar en una segunda fila si hay espacio
  const additionalStats = [
    {
      title: 'Distribuidores Inactivos',
      value: stats.inactive || 0,
      icon: <Cancel sx={{ fontSize: '2rem', color: THEME_COLORS.TEXT_MUTED }} />,
      color: THEME_COLORS.TEXT_MUTED,
    },
    {
      title: 'Contactos Sin Asignar',
      value: stats.total_contacts_unassigned || 0,
      icon: <People sx={{ fontSize: '2rem', color: THEME_COLORS.SECONDARY }} />,
      color: THEME_COLORS.SECONDARY,
    },
    {
      title: 'Porcentaje Activos',
      value: `${stats.active_percentage || 0}%`,
      icon: <CheckCircle sx={{ fontSize: '2rem', color: THEME_COLORS.TEXT_SECONDARY }} />,
      color: THEME_COLORS.TEXT_SECONDARY,
    },
    {
      title: 'Porcentaje Asignación',
      value: `${stats.assignment_percentage || 0}%`,
      icon: <People sx={{ fontSize: '2rem', color: THEME_COLORS.TEXT_PRIMARY }} />,
      color: THEME_COLORS.TEXT_PRIMARY,
    },
  ];

  return (
    <Box sx={{ marginBottom: 4 }}>
      {/* Estadísticas principales */}
      <Grid container spacing={3} sx={{ marginBottom: 2 }}>
        {statsData.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                border: `1px solid ${THEME_COLORS.BORDER}`,
                borderRadius: '12px',
                backgroundColor: THEME_COLORS.WHITE,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                  borderColor: stat.color,
                },
              }}
            >
              <CardContent sx={{ padding: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        color: stat.color,
                        fontWeight: 700,
                        fontSize: { xs: '1.75rem', sm: '2rem' },
                        marginBottom: 0.5,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: THEME_COLORS.TEXT_SECONDARY,
                        fontWeight: 500,
                        fontSize: '0.875rem',
                      }}
                    >
                      {stat.title}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 60,
                      height: 60,
                      borderRadius: '12px',
                      backgroundColor: `${stat.color}15`,
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Estadísticas adicionales - Solo mostrar si hay datos relevantes */}
      {(stats.total > 0) && (
        <Grid container spacing={3}>
          {additionalStats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={`additional-${index}`}>
              <Card
                sx={{
                  height: '100%',
                  border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
                  borderRadius: '12px',
                  backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                    borderColor: stat.color,
                  },
                }}
              >
                <CardContent sx={{ padding: 2.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          color: stat.color,
                          fontWeight: 600,
                          fontSize: { xs: '1.25rem', sm: '1.5rem' },
                          marginBottom: 0.5,
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: THEME_COLORS.TEXT_MUTED,
                          fontWeight: 500,
                          fontSize: '0.8rem',
                        }}
                      >
                        {stat.title}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 50,
                        height: 50,
                        borderRadius: '10px',
                        backgroundColor: `${stat.color}10`,
                      }}
                    >
                      {React.cloneElement(stat.icon, { 
                        sx: { fontSize: '1.5rem', color: stat.color } 
                      })}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default DistributorStats;